import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Eye, EyeOff, Plus, Settings, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useReadOnlyGuard } from '@/hooks/useReadOnlyGuard';
import type { BuiltInPageSlug } from '@/hooks/usePageExtraSections';
import { SectionRenderer, type SectionData } from '@/components/sections/SectionRenderer';
import { BUILT_IN_PAGE_EXTRA_TYPES, SECTION_TYPES, getDefaultContent } from '@/components/sections/section-types';
import { SectionEditor } from '@/components/admin/SectionEditor';
import { cn } from '@/lib/utils';

const EXTRA_TYPES = SECTION_TYPES.filter((type) => (BUILT_IN_PAGE_EXTRA_TYPES as readonly string[]).includes(type.id));

/**
 * Optional page-builder blocks for the built-in pages (home / about /
 * services), stored in page_sections by page_slug. Unlike the built-in page
 * content these persist as you edit (add, settings, reorder, hide, delete) —
 * the live page shows them just above its closing call-to-action.
 */
export function BuiltInPageExtras({ pageSlug }: { pageSlug: BuiltInPageSlug }) {
  const { orgId, canWrite } = useAdminOrg();
  const { assertCanWrite } = useReadOnlyGuard();
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const queryKey = ['builder-page-extras', orgId, pageSlug];
  const { data: sections = [] } = useQuery({
    queryKey,
    enabled: Boolean(orgId),
    queryFn: async (): Promise<SectionData[]> => {
      const { data, error } = await supabase
        .from('page_sections')
        .select('id, section_type, content, layout_variant, is_visible, sort_order')
        .eq('organization_id', orgId!)
        .eq('page_slug', pageSlug)
        // Ignore legacy provision_org rows (hero/services) — only optional blocks are managed here.
        .in('section_type', [...BUILT_IN_PAGE_EXTRA_TYPES])
        .order('sort_order');
      if (error) throw error;
      return (data ?? []).map((section) => ({
        ...section,
        content: (section.content || {}) as Record<string, unknown>,
        layout_variant: section.layout_variant || 'default',
      }));
    },
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({ queryKey: ['page-extra-sections', orgId, pageSlug] });
  };

  const run = async (action: () => PromiseLike<{ error: unknown }>, success: string, failure: string) => {
    if (!assertCanWrite()) return false;
    const { error } = await action();
    if (error) {
      console.error(failure, error);
      toast.error(failure);
      return false;
    }
    toast.success(success);
    refresh();
    return true;
  };

  const addBlock = async (sectionType: string) => {
    if (!orgId) return;
    const ok = await run(
      () =>
        supabase.from('page_sections').insert([{
          organization_id: orgId,
          page_id: null,
          page_slug: pageSlug,
          section_type: sectionType,
          content: getDefaultContent(sectionType) as Json,
          layout_variant: 'default',
          is_visible: true,
          sort_order: sections.length,
        }]),
      'Block added',
      'Failed to add block',
    );
    if (ok) setShowAdd(false);
  };

  const moveBlock = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= sections.length) return;
    const reordered = [...sections];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    await run(
      async () => {
        const results = await Promise.all(
          reordered.map((section, order) => supabase.from('page_sections').update({ sort_order: order }).eq('id', section.id)),
        );
        return { error: results.find((result) => result.error)?.error ?? null };
      },
      'Block moved',
      'Failed to move block',
    );
  };

  const editing = sections.find((section) => section.id === editingId) ?? null;
  const typeName = (id: string) => SECTION_TYPES.find((type) => type.id === id)?.name ?? id;

  return (
    <div className="border-t border-dashed border-border bg-muted/20" data-testid="page-extra-blocks">
      <div className="container mx-auto flex flex-wrap items-center justify-between gap-4 px-5 py-6 md:px-6">
        <div>
          <p className="text-sm font-semibold text-foreground">Optional blocks</p>
          <p className="text-xs text-muted-foreground">
            Shown just above this page&apos;s closing call-to-action. Changes here save straight away.
          </p>
        </div>
        <Button variant="outline" size="sm" disabled={!canWrite} onClick={() => { if (assertCanWrite()) setShowAdd(true); }}>
          <Plus className="mr-2 h-4 w-4" />
          Add block
        </Button>
      </div>

      {sections.map((section, index) => (
        <div key={section.id} className={cn('group relative', !section.is_visible && 'opacity-50')}>
          <div className="absolute right-4 top-4 z-10 flex items-center gap-1 rounded-lg border border-border bg-background/95 p-1 opacity-0 shadow-lg backdrop-blur transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
            <span className="px-2 text-xs font-medium text-muted-foreground">
              {typeName(section.section_type)}{!section.is_visible && ' (hidden)'}
            </span>
            <div className="h-4 w-px bg-border" />
            <Button variant="ghost" size="icon" className="h-7 w-7" disabled={!canWrite} onClick={() => setEditingId(section.id)} aria-label="Block settings" title="Block settings">
              <Settings className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" disabled={!canWrite || index === 0} onClick={() => moveBlock(index, -1)} aria-label="Move block up">
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" disabled={!canWrite || index === sections.length - 1} onClick={() => moveBlock(index, 1)} aria-label="Move block down">
              <ChevronDown className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              disabled={!canWrite}
              aria-label={section.is_visible ? 'Hide block' : 'Show block'}
              onClick={() =>
                run(
                  () => supabase.from('page_sections').update({ is_visible: !section.is_visible }).eq('id', section.id),
                  section.is_visible ? 'Block hidden' : 'Block shown',
                  'Failed to update block',
                )
              }
            >
              {section.is_visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive hover:text-destructive"
              disabled={!canWrite}
              aria-label="Delete block"
              onClick={() => run(() => supabase.from('page_sections').delete().eq('id', section.id), 'Block deleted', 'Failed to delete block')}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          {/* Preview hidden blocks too (dimmed) so they can be switched back on. */}
          <SectionRenderer section={{ ...section, is_visible: true }} preview />
        </div>
      ))}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add a block</DialogTitle>
            <DialogDescription>Optional extras for this page.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 pt-4">
            {EXTRA_TYPES.map((type) => (
              <Button key={type.id} variant="outline" className="h-auto justify-start px-4 py-3" disabled={!canWrite} onClick={() => addBlock(type.id)}>
                <div className="text-left">
                  <div className="font-medium">{type.name}</div>
                  <div className="text-xs text-muted-foreground">{type.description}</div>
                </div>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editing !== null} onOpenChange={(open) => { if (!open) setEditingId(null); }}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? typeName(editing.section_type) : 'Block'} settings</DialogTitle>
            <DialogDescription>Saved to your live page as soon as you press Save.</DialogDescription>
          </DialogHeader>
          {editing && (
            <SectionEditor
              key={editing.id}
              section={editing}
              onCancel={() => setEditingId(null)}
              onSave={async (next) => {
                const ok = await run(
                  () =>
                    supabase
                      .from('page_sections')
                      .update({ content: next.content as Json, layout_variant: next.layout_variant })
                      .eq('id', next.id),
                  'Block saved',
                  'Failed to save block',
                );
                if (ok) setEditingId(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
